import { PO_APPROVAL_LIMIT } from '@palaccount/contracts';
import { Money } from '@palaccount/money';

/**
 * The pure client-side mirrors of PUR-01/PUR-03's computed facts. The
 * SERVER is the authority (needsApproval, matchStatus, overBilled and the
 * send door's refusals travel on the responses); these mirrors keep the
 * editor honest between keystrokes.
 */

/**
 * BR-PUR-01-01/-04 mirrored: strictly ABOVE the limit, while draft. Exactly
 * at the limit needs no approval (PUR-01 §15's boundary). The limit is the
 * company's `treasury.po_approval_limit` setting — callers pass the value
 * from useOrgSetting; the registry default is only the loading fallback.
 */
export function needsApproval(
  grandTotal: string,
  currency = 'USD',
  limit: string = PO_APPROVAL_LIMIT,
): boolean {
  try {
    return Money.of(grandTotal, currency).greaterThan(Money.of(limit, currency));
  } catch {
    return false;
  }
}

/**
 * Wave 1C — BR-PUR-03-12's tolerance band, mirrored exactly as the server
 * applies it (`purchasing.bill_variance_tolerance_pct`): a bill within this
 * percentage of the EXPECTED figure is matched; "0" is strict equality to
 * the cent. Measured on the expected side, so a bill cannot widen its own
 * band by overstating.
 */
export function withinTolerance(actual: Money, expected: Money, tolerancePct: string): boolean {
  if (actual.equals(expected)) return true;
  let pct: Money;
  try {
    pct = Money.of(tolerancePct, actual.currency);
  } catch {
    return false;
  }
  if (pct.isZero()) return false;
  const band = expected.abs().times(tolerancePct).dividedBy('100');
  return !actual.minus(expected).abs().greaterThan(band);
}

/**
 * §7.5 mirrored, the computable pair: the bill against what is still OPEN
 * on its order — the order's grand total less the bills already posted on
 * it (`orderedOpen` on the bill response; wave 1C's correction of the
 * second "matched" bill) — within the company's tolerance (BR-PUR-03-12).
 * No order = a service bill (BR-PUR-03-15). The received leg comes from the
 * API's receivedTotal — Σ accepted value over the order's POSTED goods
 * receipts (PUR-02) — and that verdict is the server's.
 */
export function matchVerdict(
  billedTotal: string,
  orderedOpen: string | null,
  currency = 'USD',
  tolerancePct = '0',
): 'matched' | 'variance' | 'no_order' {
  if (orderedOpen === null) return 'no_order';
  try {
    return withinTolerance(
      Money.of(billedTotal, currency),
      Money.of(orderedOpen, currency),
      tolerancePct,
    )
      ? 'matched'
      : 'variance';
  } catch {
    return 'variance';
  }
}

/**
 * Wave 1C — the billed figure's tone on the order strip and the list: green
 * only when billed EQUALS ordered (within FULLY_PAID_TOLERANCE, the server's
 * `fullyBilled`), amber when MORE was billed than ordered (`overBilled`),
 * plain otherwise. Both flags are the server's; this only agrees on which
 * wins when both are true — over-billed is never green.
 */
export function billedTone(order: {
  fullyBilled: boolean;
  overBilled: boolean;
}): 'over' | 'full' | 'open' {
  if (order.overBilled) return 'over';
  if (order.fullyBilled) return 'full';
  return 'open';
}

/**
 * Wave 1C — the send door's own precondition, mirrored (`PO_MESSAGES.
 * expectedDateRequired`): a commitment carries the date the late flag and
 * the chase nudge read (BR-PUR-01-10). Blank or whitespace is no date.
 */
export function canSend(order: { expectedDate: string | null | undefined }): boolean {
  return typeof order.expectedDate === 'string' && order.expectedDate.trim() !== '';
}
